/**
 * Mangys wholesale shipping quote — cart drawer client.
 *
 * UX:
 *  - Carriers list is contained in a fixed-height scrollable box (NEVER
 *    expands the cart drawer past viewport).
 *  - On carrier select: directly MODIFY the theme's "Total estimado" value
 *    (no new rows) so the displayed total = product subtotal + shipping.
 *  - Changing carrier updates the total in real time.
 *  - Intercepts the GLOBAL "Pagar pedido" button to attach destination + go
 *    to checkout with the address prefilled.
 */

(function () {
  'use strict';

  const STATE_MAP = {
    'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA',
    'colorado':'CO','connecticut':'CT','delaware':'DE','florida':'FL','georgia':'GA',
    'hawaii':'HI','idaho':'ID','illinois':'IL','indiana':'IN','iowa':'IA',
    'kansas':'KS','kentucky':'KY','louisiana':'LA','maine':'ME','maryland':'MD',
    'massachusetts':'MA','michigan':'MI','minnesota':'MN','mississippi':'MS','missouri':'MO',
    'montana':'MT','nebraska':'NE','nevada':'NV','new hampshire':'NH','new jersey':'NJ',
    'new mexico':'NM','new york':'NY','north carolina':'NC','north dakota':'ND','ohio':'OH',
    'oklahoma':'OK','oregon':'OR','pennsylvania':'PA','rhode island':'RI','south carolina':'SC',
    'south dakota':'SD','tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT',
    'virginia':'VA','washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY',
    'district of columbia':'DC','puerto rico':'PR',
    'nueva york':'NY','nuevo mexico':'NM','nuevo méxico':'NM','carolina del norte':'NC',
    'carolina del sur':'SC','dakota del norte':'ND','dakota del sur':'SD',
  };

  function normalizeState(raw) {
    const s = (raw || '').toString().trim();
    if (!s) return '';
    if (s.length === 2) return s.toUpperCase();
    const key = s.toLowerCase().replace(/\s+/g, ' ');
    return STATE_MAP[key] || s.toUpperCase();
  }

  // Module-level state — survives re-renders of the cart drawer
  const STATE = { destination: null, selected: null, shipmentSummary: null };

  function findTotalEl() {
    return document.querySelector(
      'cart-drawer .totals__subtotal-value, .cart-drawer .totals__subtotal-value, ' +
      '#CartDrawer .totals__subtotal-value, .totals__subtotal-value'
    );
  }

  // Modify the theme's "Total estimado" value to be subtotal + shipping.
  // Stores the original on first run and uses it as the basis for recompute.
  function updateTotalEl() {
    const el = findTotalEl();
    if (!el) return;
    // Stash original on first touch
    if (!el.dataset.mltlOriginal) {
      el.dataset.mltlOriginal = el.textContent.trim();
    }
    const original = el.dataset.mltlOriginal;
    const m = original.replace(/,/g, '').match(/([0-9]+(?:\.[0-9]+)?)/);
    if (!m) return;
    const subtotalUsd = parseFloat(m[1]);
    const shippingUsd = STATE.selected ? (parseFloat(STATE.selected.price) || 0) : 0;
    const newTotal = subtotalUsd + shippingUsd;
    const formatted = newTotal.toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
    // Replace the numeric part of the original string, keep the rest (currency suffix etc)
    el.textContent = original.replace(/[\d,]+(?:\.\d+)?/, formatted);
  }

  function resetTotalEl() {
    const el = findTotalEl();
    if (el && el.dataset.mltlOriginal) {
      el.textContent = el.dataset.mltlOriginal;
    }
  }

  async function saveAttributes() {
    const sel = STATE.selected;
    const dest = STATE.destination || {};
    if (!sel) return;
    try {
      // 1. Cart attributes — for our own server's reference (BOL booking, etc).
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            ltl_quote_id: sel.quoteId,
            ltl_carrier: sel.carrier,
            ltl_price_usd: sel.price,
            ltl_service_days: sel.days || '',
            ltl_dest_zip: dest.zip || '',
            ltl_dest_state: dest.state || '',
            // Physical shipment details for the warehouse person who creates the
            // BOL manually in GTZ. Shows in the order under "Additional details".
            'Datos para crear el envío (LTL)':
              (STATE.shipmentSummary && STATE.shipmentSummary.text)
                ? `${sel.carrier} · ${STATE.shipmentSummary.text}`
                : `${sel.carrier} · ${dest.state || ''} ${dest.zip || ''}`,
          },
        }),
      });

      // 2. Line item property on the FIRST item — this is the channel that
      // Shopify forwards to our /api/shopify/carrier-service callback. Our
      // server reads `_ltl_quote_id` and returns ONLY that carrier so the
      // checkout pre-selects it. Properties starting with underscore are
      // hidden from the customer.
      await setFirstItemLtlProperties({
        _ltl_quote_id: String(sel.quoteId || ''),
        _ltl_carrier: String(sel.carrier || ''),
      });
    } catch (err) {
      console.warn('[mltl] cart attribute save failed:', err);
    }
  }

  // Set hidden LTL properties on the FIRST line item, preserving any other
  // properties the theme/apps may have set (e.g. _is_paca).
  async function setFirstItemLtlProperties(extra) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(r => r.json());
      if (!cart?.items?.length) return;
      const item = cart.items[0];
      const merged = Object.assign({}, item.properties || {}, extra);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: 1, properties: merged }),
      });
    } catch (err) {
      console.warn('[mltl] line item property update failed:', err);
    }
  }

  // Intercept the cart drawer's global checkout button to add ?checkout[shipping_address]
  // params so the customer's destination is prefilled.
  function hookCheckoutButton() {
    const btn = document.querySelector(
      'cart-drawer #CartDrawer-Checkout, .cart-drawer button[name="checkout"], ' +
      '#CartDrawer-Checkout, button[name="checkout"]'
    );
    if (!btn || btn.__mltlHooked) return;
    btn.__mltlHooked = true;
    btn.addEventListener('click', function (e) {
      const dest = STATE.destination;
      if (!dest?.zip) return;
      e.preventDefault();
      e.stopPropagation();
      saveAttributes().finally(() => {
        const params = new URLSearchParams();
        if (dest.zip) params.set('checkout[shipping_address][zip]', dest.zip);
        if (dest.state) params.set('checkout[shipping_address][province_code]', dest.state);
        if (dest.country) params.set('checkout[shipping_address][country_code]', dest.country);
        window.location.href = '/checkout' + (params.toString() ? '?' + params.toString() : '');
      });
    }, true);
  }

  async function runQuote(root) {
    if (!root) return;
    const endpoint = root.dataset.endpoint;
    const pacaCount = parseInt(root.dataset.pacaCount, 10) || 0;
    const results = root.querySelector('[data-mltl-results]');
    const submitBtn = root.querySelector('[data-mltl-submit]');
    const confirm = root.querySelector('[data-mltl-confirm]');
    if (!results) return;

    results.hidden = true;
    results.innerHTML = '';
    if (confirm) confirm.hidden = true;

    const country = (root.querySelector('#mltl-country')?.value || 'US').toString();
    const state = normalizeState(root.querySelector('#mltl-state')?.value);
    const zip = (root.querySelector('#mltl-zip')?.value || '').toString().trim();

    if (!/^\d{5}$/.test(zip)) {
      renderError(results, 'Ingresa un código postal de 5 dígitos.');
      return;
    }

    STATE.destination = { country, state, zip };

    setLoading(submitBtn, true);
    try {
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pacaCount,
          destination: { zip, country, state },
        }),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || !data.qualifies) {
        if (data.reason === 'non_us_destination') {
          renderError(results, 'Solo disponible para envíos dentro de Estados Unidos.');
        } else if (data.reason === 'missing_zip') {
          renderError(results, 'El código postal es requerido.');
        } else if (data.reason === 'below_minimum') {
          renderError(results, `Necesitas ${data.minRequired || 5} productos mínimo.`);
        } else {
          renderError(results, 'No pudimos cotizar el envío en este momento. Continúa al checkout o contáctanos.');
        }
        return;
      }

      STATE.shipmentSummary = data.shipmentSummary || null;
      renderQuotes(root, results, data.quotes || []);
    } catch (err) {
      console.warn('[mltl] estimate failed:', err);
      renderError(results, 'No pudimos cotizar el envío en este momento. Continúa al checkout o contáctanos.');
    } finally {
      setLoading(submitBtn, false);
    }
  }

  function renderQuotes(root, container, quotes) {
    container.hidden = false;
    if (!quotes.length) {
      container.innerHTML = '<div class="mltl__error">No hay tarifas disponibles para este destino.</div>';
      return;
    }
    const top = quotes.slice(0, 12);
    container.innerHTML =
      `<div class="mltl__carriers-label">Elegí transportista <span class="mltl__carriers-hint">(${top.length} disponibles · deslizá para ver más)</span></div>` +
      '<div class="mltl__carriers" data-mltl-carriers>' +
      top.map((q) => {
        const price = '$' + Number(q.priceUsd).toFixed(2);
        const carrier = escapeHtml(q.carrier || 'Carrier');
        const daysLabel = q.serviceDays
          ? `${q.serviceDays} ${q.serviceDays === 1 ? 'día hábil' : 'días hábiles'}`
          : '';
        return `
          <button type="button" class="mltl__carrier" data-mltl-carrier
            data-quote-id="${escapeHtml(String(q.quoteId || ''))}"
            data-carrier="${carrier}"
            data-price="${Number(q.priceUsd).toFixed(2)}"
            data-days="${q.serviceDays || ''}">
            <span class="mltl__carrier-radio" aria-hidden="true"></span>
            <span class="mltl__carrier-info">
              <span class="mltl__carrier-name">${carrier}</span>
              ${daysLabel ? `<span class="mltl__carrier-meta">${daysLabel}</span>` : ''}
            </span>
            <span class="mltl__carrier-price">${price} USD</span>
          </button>
        `;
      }).join('') +
      '</div>';

    container.querySelectorAll('[data-mltl-carrier]').forEach((btn) => {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        selectCarrier(root, btn);
      });
    });

    // Auto-select cheapest
    const first = container.querySelector('[data-mltl-carrier]');
    if (first) selectCarrier(root, first);
  }

  function selectCarrier(root, btn) {
    root.querySelectorAll('[data-mltl-carrier]').forEach((el) => el.classList.remove('is-selected'));
    btn.classList.add('is-selected');
    STATE.selected = {
      quoteId: btn.dataset.quoteId,
      carrier: btn.dataset.carrier,
      price: btn.dataset.price,
      days: btn.dataset.days,
    };

    // Confirmation message
    const confirm = root.querySelector('[data-mltl-confirm]');
    const confirmText = root.querySelector('[data-mltl-confirm-text]');
    if (confirm && confirmText) {
      const priceStr = '$' + Number(STATE.selected.price).toFixed(2) + ' USD';
      confirmText.textContent = `${STATE.selected.carrier} · ${priceStr} — envío sumado al total. Tocá "Pagar pedido" para continuar.`;
      confirm.hidden = false;
    }

    // Save attrs + update total in real time
    saveAttributes();
    updateTotalEl();
    hookCheckoutButton();
  }

  function init(root) {
    if (!root || root.__mltlInitialized) return;
    root.__mltlInitialized = true;

    const submitBtn = root.querySelector('[data-mltl-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        runQuote(root);
      });
    }

    root.querySelectorAll('input, select').forEach((el) => {
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          runQuote(root);
        }
      });
    });

    hookCheckoutButton();
  }

  // Safety net for clicks before init binds
  document.addEventListener('click', function (e) {
    const sBtn = e.target.closest('[data-mltl-submit]');
    if (sBtn) {
      const root = sBtn.closest('#mangys-ltl-quote, .mltl');
      if (root && !root.__mltlInitialized) {
        e.preventDefault();
        e.stopPropagation();
        init(root);
        runQuote(root);
      }
    }
  }, true);

  function setLoading(btn, on) {
    if (!btn) return;
    btn.disabled = !!on;
    btn.dataset.loading = on ? 'true' : 'false';
  }

  function renderError(container, message) {
    container.hidden = false;
    container.innerHTML = `<div class="mltl__error">${escapeHtml(message)}</div>`;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CART DRAWER LOCK
  // When the cart drawer is open: freeze the page behind it (body.overflow:hidden)
  // so the user can scroll ONLY the cart contents, not the storefront below.
  // When the drawer closes: unlock the body and restore the previous scroll position.
  // ─────────────────────────────────────────────────────────────────────────

  let DRAWER_LOCK = { savedScrollY: 0, isLocked: false };

  function isDrawerOpen(drawer) {
    if (!drawer) return false;
    if (drawer.classList.contains('active')) return true;
    if (drawer.hasAttribute('open')) return true;
    if (drawer.getAttribute('aria-hidden') === 'false') return true;
    // Some Dawn forks use a class on <body> or <html>
    if (document.body.classList.contains('overflow-hidden')) return true;
    if (document.documentElement.classList.contains('cart-drawer-open')) return true;
    return false;
  }

  function lockBody() {
    if (DRAWER_LOCK.isLocked) return;
    DRAWER_LOCK.isLocked = true;
    // The body lock applies only on mobile (CSS media query handles the gating).
    // We just toggle classes — touch-action:none on body blocks background scroll on iOS,
    // and touch-action:auto on drawer descendants allows internal scroll.
    document.documentElement.classList.add('mltl-drawer-open');
    document.body.classList.add('mltl-drawer-open');
  }

  function unlockBody() {
    if (!DRAWER_LOCK.isLocked) return;
    DRAWER_LOCK.isLocked = false;
    document.documentElement.classList.remove('mltl-drawer-open');
    document.body.classList.remove('mltl-drawer-open');
  }

  function setupDrawerLock() {
    const drawer = document.querySelector('cart-drawer, .cart-drawer, #CartDrawer');
    if (!drawer || drawer.__mltlLockHooked) return;
    drawer.__mltlLockHooked = true;

    // Initial state
    if (isDrawerOpen(drawer)) lockBody();

    // Observe class / attribute changes on the drawer element itself
    const observer = new MutationObserver(() => {
      if (isDrawerOpen(drawer)) lockBody();
      else unlockBody();
    });
    observer.observe(drawer, {
      attributes: true,
      attributeFilter: ['class', 'open', 'aria-hidden'],
    });

    // Also observe the documentElement / body in case the theme toggles classes there
    const rootObserver = new MutationObserver(() => {
      if (isDrawerOpen(drawer)) lockBody();
      else unlockBody();
    });
    rootObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    rootObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Theme events
    ['cart-drawer:opened', 'cart:open', 'cart:opened'].forEach((evt) => {
      document.addEventListener(evt, () => setTimeout(() => {
        if (isDrawerOpen(drawer)) lockBody();
      }, 30));
    });
    ['cart-drawer:closed', 'cart:close', 'cart:closed'].forEach((evt) => {
      document.addEventListener(evt, () => setTimeout(unlockBody, 30));
    });

    // ESC key — common close pattern
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && DRAWER_LOCK.isLocked) {
        setTimeout(() => {
          if (!isDrawerOpen(drawer)) unlockBody();
        }, 50);
      }
    });
  }

  function bootAll() {
    document.querySelectorAll('#mangys-ltl-quote, .mltl').forEach(init);
    hookCheckoutButton();
    setupDrawerLock();
    if (STATE.selected) setTimeout(updateTotalEl, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootAll);
  } else {
    bootAll();
  }

  ['cart:updated', 'cart:refresh', 'cart-drawer:opened', 'cart:open'].forEach((evt) => {
    document.addEventListener(evt, () => setTimeout(bootAll, 50));
  });
})();
